<template>
  <div :class="['flex flex-col space-y-2 justify-between text-sm',
    this.borderless ? 'pt-1' : 'border border-[#1e3c53] border-opacity-30 rounded-xl px-3 py-1']">
    <div class="flex-col space-y-1">
      <div class="flex items-center justify-center place-items-center space-x-4">
        <i class="min-w-max h-10">
        <img
          v-if="section?.icon"
          :src="sectionIcon(section.icon)"
          :class="this.reduced ? 'w-8 h-8' : 'w-10 h-10'"
          alt="section icon">
        </i>
        <div class="text-nowrap">{{ this.$t(this.getSectionHeaderKey()) }}</div>
      </div>
      <div
        v-for="(content, index) in section?.values ?? []"
        :key="content?.value"
        class="flex justify-between text-left">
        <div class="flex items-center gap-2">
          <span v-if="content?.label" class="text-xs text-gray-500 min-w-[110px] text-left">{{ this.$t(content.label) }}</span>
          <div :contenteditable="this.editable"
            @keydown="preventReturn"
            @blur="updateValue($event, index)"
            :class="['flex rounded-md p-1 -m-1 text-nowrap transition-colors ease-in-out duration-150',
              this.editable ? 'hover:bg-gray-200 cursor-pointer' : '']"
            spellcheck="false">
            <i v-if="content?.edited">
              <svg width="12px" height="12px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048"></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M13 21H21" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M20.0651 7.39423L7.09967 20.4114C6.72438 20.7882 6.21446 21 5.68265 21H4.00383C3.44943 21 3 20.5466 3
                  19.9922V18.2987C3 17.7696 3.20962 17.2621 3.58297 16.8873L16.5517 3.86681C19.5632 1.34721 22.5747 4.87462 20.0651
                  7.39423Z" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M15.3097 5.30981L18.7274 8.72755" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </g>
              </svg>
            </i>
            {{ content?.value.replaceAll('_', ' ') ?? '' }}
          </div>
        </div>
        <i class="min-w-max h-5">
          <img v-if="content.check_digit_passed"
            :src="confidenceIcon(content?.confidence)"
            v-on:mouseenter="confidenceHovered = index"
            v-on:mouseleave="confidenceHovered = -1"
            v-on:focusin="confidenceHovered = index"
            v-on:focusout="confidenceHovered = -1"
            :class="['self-center ml-2 transition-transform ease-in duration-300',
              this.reduced ? 'w-3 h-3' : 'w-5 h-5',
              this.confidenceHovered === index? 'scale-110' : '']"
            alt="confidence icon"
            :title=confidenceRounded(content?.confidence)>
          <img v-else :src="sectionIcon('alertIcon')"
            :class="[this.reduced ? 'w-4 h-4' : 'w-5 h-5', 'self-center ml-2']"
            alt="confidence icon"
            :title=content.check_digit_str>
        </i>
      </div>
    </div>
  </div>
</template>

<script>
import { getConfidenceIcon, getIcon } from '@/utils/icons';
import valueToPercent from '@/utils/math';
import { getStatisticalTypeName } from '@/utils/lookup';

export default {
  name: 'CardSection',
  props: {
    section: {
      type: Object,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
      default: -1,
    },
    borderless: {
      type: Boolean,
      default: false,
      required: false,
    },
    reduced: {
      type: Boolean,
      default: false,
      required: false,
    },
    editable: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  setup() {
    const confidenceIcon = getConfidenceIcon;
    const confidenceRounded = valueToPercent;
    const getStatsTypeName = getStatisticalTypeName;
    const sectionIcon = getIcon;
    return {
      confidenceIcon,
      confidenceRounded,
      sectionIcon,
      getStatsTypeName,
    };
  },
  data() {
    return {
      confidenceHovered: -1,
    };
  },
  methods: {
    // Resolves the i18n key for this section's panel header: either the
    // reader-specific override (see TransitCard's getIntermodalMarkingRailHeader)
    // or the generic stats-type name. Kept out of the template to stay under max-len.
    getSectionHeaderKey() {
      return this.section.customHeader ?? this.getStatsTypeName(this.section.type);
    },
    updateValue(event, index) {
      const updatedValue = event.target.innerText;
      this.$emit('update-value', { index, updatedValue, sectionIndex: this.sectionIndex });
    },
    preventReturn(event) {
      const keyCode = (event.keyCode ? event.keyCode : event.which);
      if (keyCode === 13) {
        event.preventDefault();
        event.target.blur();
      }
    },
  },
};
</script>
